import { useRef, useCallback } from 'react';
import { message as antMessage } from 'antd';

interface RetryOptions {
    maxRetries?: number;
    baseDelay?: number;
    onError?: (error: any) => void;
}

export const useRetry = (options: RetryOptions = {}) => {
    const {
        maxRetries = 3,
        baseDelay = 1000,
        onError
    } = options;

    const retryCountRef = useRef(0);

    const executeWithRetry = useCallback(async <T>(
        operation: () => Promise<T>,
        errorMessage: string = '操作失败'
    ): Promise<T | null> => {
        try {
            const result = await operation();
            retryCountRef.current = 0;
            return result;
        } catch (error) {
            if (retryCountRef.current < maxRetries) {
                retryCountRef.current += 1;
                const delay = baseDelay * Math.pow(2, retryCountRef.current - 1);
                
                console.error(`${errorMessage}:`, error);
                onError?.(error);

                return new Promise((resolve) => {
                    setTimeout(async () => {
                        const result = await executeWithRetry(operation, errorMessage);
                        resolve(result);
                    }, delay);
                });
            } else {
                retryCountRef.current = 0;
                antMessage.error(`${errorMessage}，请重试`);
                onError?.(error);
                return null;
            }
        }
    }, [maxRetries, baseDelay, onError]);

    const resetRetryCount = useCallback(() => {
        retryCountRef.current = 0;
    }, []);

    return {
        executeWithRetry,
        resetRetryCount,
        currentRetryCount: retryCountRef.current
    };
};